import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useProducts } from './useProducts';
import { buildCategoryGroupCards, countProductsInGroup, thumbForGroup } from './catalogGroupUtils';
import { formatDisplayTitle, formatProductTitle } from './formatProductTitle';
import { useSiteSettings } from './SiteSettingsContext';
import { resolveFeaturedProducts } from './featuredProducts';
import AddToCartButton from './cart/AddToCartButton';
import ProductLetterRibbon from './cart/ProductLetterRibbon';
import WishlistHeartButton from './wishlist/WishlistHeartButton';
import { productDetailPath } from './productPaths';
import { firstProductImage } from './catalog/catalogMedia';
import { productEffectivePrice } from './productPricing';
import CatalogImage from './catalog/CatalogImage';
import ProductPrice from './catalog/ProductPrice';
import HorizontalScrollStrip from './HorizontalScrollStrip';

/**
 * Home sections driven by the storefront catalog (MySQL → /api/catalog.php).
 */
export default function HomeDynamicCatalog() {
  const { categories, products, allProducts, loading } = useProducts();
  const { settings: site } = useSiteSettings();

  const sortedCats = useMemo(
    () =>
      [...categories].sort((a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug)),
    [categories]
  );

  /** All categories — layout uses CSS Grid (auto-fit) so any count aligns evenly without empty slots. */
  const topCategories = useMemo(() => sortedCats, [sortedCats]);

  const groupTiles = useMemo(() => {
    const tiles = [];
    for (const c of sortedCats) {
      const list = products[c.slug] || [];
      if (!list.length) continue;
      const groups = buildCategoryGroupCards(c, list);
      for (const g of groups) {
        const n = countProductsInGroup(list, g.key);
        if (n === 0) continue;
        const image = thumbForGroup(list, g.key);
        if (!image) continue;
        tiles.push({
          key: `${c.slug}-${g.key}`,
          title: g.label,
          subtitle: `${n} design${n === 1 ? '' : 's'} · ${formatDisplayTitle(c.name || c.slug)}`,
          image: thumbForGroup(list, g.key),
          to: `/shop/${c.slug}/${encodeURIComponent(g.key)}`,
        });
      }
    }
    return tiles;
  }, [sortedCats, products]);

  const spotlight = useMemo(() => groupTiles.slice(0, 12), [groupTiles]);
  const looks = useMemo(() => groupTiles.slice(0, 4), [groupTiles]);

  const featuredProducts = useMemo(
    () => resolveFeaturedProducts(allProducts, site.featured_products, 8),
    [allProducts, site.featured_products],
  );

  const home = site.page_copy?.home || {};

  if (loading) {
    return (
      <section className="collections-grid">
        <div className="container py-5 text-center text-muted">Loading collections from catalog…</div>
      </section>
    );
  }

  return (
    <>
      <section className="collections-grid">
        <div className="container store-page__container">
          <h2 className="section-title home-collections-title">{home.collections_title}</h2>
          <p className="home-collections-lead text-center text-muted">{home.collections_lead}</p>

          <div className="home-collection-grid home-collection-grid--categories">
            {topCategories.map((c) => {
              const list = products[c.slug] || [];
              const img = firstProductImage(list);
              const n = list.length;
              return (
                <div className="home-collection-grid__cell" key={c.slug}>
                  <div className="collection-card w-100">
                    <div className="collection-image-wrapper">
                      <CatalogImage
                        src={img}
                        alt={formatDisplayTitle(c.name || c.slug)}
                        className="collection-image"
                        sizes="(max-width: 576px) 100vw, (max-width: 992px) 50vw, 25vw"
                      />
                      <div className="collection-overlay">
                        <Link to={`/shop/${c.slug}`} className="btn btn-secondary">
                          Shop {formatDisplayTitle(c.name || c.slug)}
                        </Link>
                      </div>
                    </div>
                    <h3 className="collection-title">{formatDisplayTitle(c.name || c.slug)}</h3>
                    <p className="collection-desc">
                      {n > 0 ? `${n} handcrafted design${n === 1 ? '' : 's'}` : 'Coming soon'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {spotlight.length > 0 && (
            <div className="home-shop-by-style">
              <h3 className="home-shop-by-style-title">{home.shop_by_style_title}</h3>
              <p className="home-shop-by-style-lead">{home.shop_by_style_lead}</p>
              <div className="home-collection-grid home-collection-grid--styles">
                {spotlight.map((t) => (
                  <div className="home-collection-grid__cell" key={t.key}>
                    <div className="collection-card collection-card--style w-100">
                      <div className="collection-image-wrapper">
                        <CatalogImage
                          src={t.image}
                          alt={t.title}
                          className="collection-image"
                          sizes="(max-width: 576px) 100vw, (max-width: 992px) 50vw, 25vw"
                        />
                        <div className="collection-overlay">
                          <Link to={t.to} className="btn btn-secondary btn-sm">
                            Shop
                          </Link>
                        </div>
                      </div>
                      <h4 className="collection-title collection-title--compact">{t.title}</h4>
                      <p className="collection-desc collection-desc--compact">{t.subtitle}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="celebrity-gallery">
        <div className="container store-page__container">
          <div className="section-header text-center mb-4">
            <h2 className="section-title">{home.curated_title}</h2>
            <p className="section-subtitle">
              {site.featured_products?.length
                ? home.curated_subtitle_featured
                : home.curated_subtitle_default}
            </p>
          </div>
          {looks.length === 0 ? (
            <p className="text-center text-muted">Add products with types or subcategories to see picks here.</p>
          ) : (
            <div className="row g-4">
              {looks.map((item) => (
                <div className="col-md-6 col-lg-3" key={item.key}>
                  <div className="celebrity-card">
                    <div className="celebrity-image-wrapper">
                      <CatalogImage src={item.image} alt={item.title} className="celebrity-image" />
                    </div>
                    <div className="celebrity-content">
                      <h3 className="celebrity-title">{item.title}</h3>
                      <p className="celebrity-desc">{item.subtitle}</p>
                      <Link to={item.to} className="btn btn-secondary btn-sm">
                        Shop this look
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="featured-products">
        <div className="container store-page__container">
          <h2 className="section-title">{home.featured_title}</h2>
          {featuredProducts.length === 0 ? (
            <p className="text-muted text-center">No products in catalog yet.</p>
          ) : (
            <HorizontalScrollStrip
              className="featured-scroll-wrap"
              trackClassName="featured-scroll"
              ariaLabel="Featured products"
            >
              {featuredProducts.map((p) => (
                <div className="featured-item" key={`${p.category}-${p.id}`}>
                  <div className="product-card featured">
                    <div className="product-image-wrapper">
                      <ProductLetterRibbon product={p} />
                      <WishlistHeartButton
                        product={p}
                        categorySlug={p.category}
                        size="sm"
                        className="product-card__wishlist"
                      />
                      <Link to={productDetailPath(p)}>
                        <CatalogImage
                          src={p.image}
                          alt={formatProductTitle(p.name || '')}
                          className="product-image"
                          sizes="196px"
                        />
                      </Link>
                    </div>
                    <h4 className="product-title">
                      <Link to={productDetailPath(p)} className="text-decoration-none text-body">
                        {formatProductTitle(p.name)}
                      </Link>
                    </h4>
                    <ProductPrice product={p} className="product-price" showBadge={false} />
                    <div className="product-card__purchase">
                      {productEffectivePrice(p) > 0 ? (
                        <AddToCartButton
                          product={p}
                          categorySlug={p.category}
                          compact
                          variant="featured"
                        />
                      ) : (
                        <p className="small text-muted mb-0 mt-2 text-center">Price on request</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </HorizontalScrollStrip>
          )}
        </div>
      </section>
    </>
  );
}
